import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import ScoreDisplay from '@/components/ScoreDisplay';
import LV2Diagnostic from '@/components/LV2Diagnostic';
import LV3LegalAnalysis from '@/components/LV3LegalAnalysis';
import LV3Preview from '@/components/LV3Preview';
import DemandePiecesLetter from '@/components/DemandePiecesLetter';
import SaisineJexPack, { hasMesureExecution } from '@/components/SaisineJexPack';
import DocumentValidityWarning from '@/components/DocumentValidityWarning';
import { paymentAPI } from '@/utils/api';
import { toast } from 'sonner';
import axios from 'axios';
import { trackScoreViewed, trackPaymentInitiated } from '@/utils/analytics';
import { getCurrentVariant, EXPERIMENTS } from '@/hooks/useABTest';

const ScorePage = () => {
  const { dossierId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [tempToken, setTempToken] = useState(null);

  useEffect(() => {
    fetchDossier();
  }, [dossierId]);

  // Track score viewed when dossier is loaded with a valid score
  useEffect(() => {
    if (dossier && dossier.score !== null && dossier.score !== undefined) {
      trackScoreViewed(dossier.score, isAnonymous);
    }
  }, [dossier, isAnonymous]);

  const fetchDossier = async () => {
    const BACKEND = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    const token = localStorage.getItem('token');
    const urlParams = new URLSearchParams(location.search);
    const urlTempToken = urlParams.get('temp_token');
    const storedTempToken = sessionStorage.getItem(`temp_token_${dossierId}`);
    const currentTempToken = urlTempToken || storedTempToken;

    const goLogin = () => {
      toast.info('Veuillez vous connecter pour voir votre dossier');
      navigate(`/login?redirect=/score/${dossierId}`);
      setLoading(false);
    };
    const goExpired = () => {
      toast.error('Ce dossier a expiré. Veuillez soumettre un nouveau dossier.');
      navigate('/');
      setLoading(false);
    };

    // 1. Accès authentifié si un token est présent
    if (token) {
      try {
        const response = await axios.get(`${BACKEND}/api/dossiers/${dossierId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDossier(response.data);
        setIsAnonymous(false);
        const lv2IsEmpty = !response.data.LV2_diagnostic || Object.keys(response.data.LV2_diagnostic).length === 0;
        if (response.data.score === null || lv2IsEmpty) {
          setTimeout(fetchDossier, 3000);
        }
        setLoading(false);
        return;
      } catch (error) {
        console.error('Auth fetch error:', error);
        // Token périmé ou pas le propriétaire : basculer sur le temp_token si présent
        // (lien email d'un prospect), sinon login/expiré. Évite le cul-de-sac login.
        if (!currentTempToken) {
          return error.response?.status === 410 ? goExpired() : goLogin();
        }
        // sinon : on tombe volontairement vers l'accès anonyme ci-dessous
      }
    }

    // 2. Accès anonyme via temp_token (voie normale ET fallback d'un token périmé)
    if (currentTempToken) {
      try {
        setTempToken(currentTempToken);
        setIsAnonymous(true);
        const response = await axios.get(
          `${BACKEND}/api/dossiers/${dossierId}/preview?temp_token=${currentTempToken}`
        );
        setDossier(response.data);
        if (response.data.score === null) {
          setTimeout(fetchDossier, 3000);
        }
        setLoading(false);
        return;
      } catch (error) {
        console.error('Preview fetch error:', error);
        return error.response?.status === 410 ? goExpired() : goLogin();
      }
    }

    // 3. Ni token exploitable ni temp_token → login
    goLogin();
  };

  const handlePayment = async () => {
    // Track payment initiation
    trackPaymentInitiated(9.99, 'EUR');

    // Un utilisateur anonyme ne peut pas créer de session de paiement (l'API exige
    // un compte). On l'envoie créer un compte en express : le dossier sera lié
    // automatiquement et le paiement reprendra tout seul au retour sur cette page
    // (cf. flag `resume_payment` + useEffect de reprise plus bas).
    const token = localStorage.getItem('token');
    if (!token || isAnonymous) {
      if (tempToken) {
        sessionStorage.setItem('link_dossier_id', dossierId);
        sessionStorage.setItem('link_temp_token', tempToken);
        sessionStorage.setItem('resume_payment', dossierId);
        if (dossier?.email) sessionStorage.setItem('link_dossier_email', dossier.email);
      }
      toast.info('Créez un compte en 30 secondes pour débloquer votre analyse');
      navigate(`/register?from=payment&dossier_id=${dossierId}`);
      return;
    }

    setPaymentLoading(true);
    try {
      const response = await paymentAPI.createSession(dossierId);

      // Redirection directe vers l'URL de checkout Stripe
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('URL de paiement non reçue');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Erreur lors de la création de la session de paiement');
      setPaymentLoading(false);
    }
  };

  // Reprise automatique du paiement après inscription/connexion d'un anonyme :
  // au retour sur /score/{id}, désormais authentifié et dossier lié, on relance
  // directement la session Stripe sans nouveau clic.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (
      !dossier ||
      isAnonymous ||
      !token ||
      dossier.payment_status === 'paid' ||
      dossier.payment_status === 'completed' ||
      sessionStorage.getItem('resume_payment') !== dossierId
    ) {
      return;
    }
    sessionStorage.removeItem('resume_payment');
    (async () => {
      setPaymentLoading(true);
      try {
        const response = await paymentAPI.createSession(dossierId);
        if (response.data.url) {
          window.location.href = response.data.url;
        } else {
          throw new Error('URL de paiement non reçue');
        }
      } catch (error) {
        console.error('Resume payment error:', error);
        toast.error('Erreur lors de la création de la session de paiement');
        setPaymentLoading(false);
      }
    })();
  }, [dossier, isAnonymous, dossierId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8fafc' }}>
        <div className="text-center max-w-2xl mx-auto px-4">
          <div className="loader mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>
            📄 Traitement de vos documents
          </h2>
          <p className="text-gray-700 text-lg mb-3">
            🔍 Extraction du texte en cours via OCR (reconnaissance optique de caractères)
          </p>
          <p className="text-gray-600 mb-2">
            Cette étape peut prendre 1 à 3 minutes selon le nombre de fichiers
          </p>
          <p className="text-sm text-gray-500 font-semibold mt-4">
            ⏳ Ne fermez pas cette page, l'analyse se poursuit en arrière-plan...
          </p>
        </div>
      </div>
    );
  }

  if (!dossier) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8fafc' }}>
        <div className="text-center">
          <p className="text-gray-600 text-lg">Dossier introuvable</p>
          <Button onClick={() => navigate('/')} className="mt-4">
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  if (dossier.score === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8fafc' }}>
        <div className="text-center max-w-2xl mx-auto px-4">
          <div className="loader mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>
            🔍 Analyse juridique en cours
          </h2>
          <p className="text-gray-700 text-lg mb-3">
            📄 Extraction du texte terminée, analyse des actes interruptifs en cours
          </p>
          <p className="text-gray-600 mb-2">
            Notre IA analyse les dates, commandements de payer, mises en demeure et autres actes juridiques
          </p>
          <p className="text-sm text-gray-500 font-semibold mt-4">
            ⏳ Encore quelques instants, ne fermez pas cette page...
          </p>
        </div>
      </div>
    );
  }

  // Extraire les données des différents niveaux d'analyse
  const lv1Data = dossier.LV1_score || {};
  const lv2Data = dossier.LV2_diagnostic || {};
  const lv3Data = dossier.LV3_analyse_juridique_payante || {};
  const documentsValidity = dossier.documents_validity;
  const isPaid = dossier.payment_status === 'paid' || dossier.payment_status === 'completed';
  // Version B (test landing_hero_v3) : l'offre est recentrée sur le courrier de
  // demande de pièces prêt à poster (pitch paywall + livrable mis en avant).
  const isVariantB = getCurrentVariant(EXPERIMENTS.LANDING_PAGE_HERO.id) === 'B';

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <Header />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Avertissement si documents non pertinents */}
        <DocumentValidityWarning documentsValidity={documentsValidity} />

        {/* LV1: Score Section - Visible pour tous */}
        <div className="card p-8 mb-8">
          <h2 className="text-2xl font-bold mb-8 text-center" style={{ color: '#1e3a8a' }}>
            Résultat de l'analyse
          </h2>
          <ScoreDisplay score={dossier.score} />

          {/* Interprétation LV1 */}
          {lv1Data.interpretation && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {lv1Data.interpretation}
              </p>
            </div>
          )}
        </div>

        {/* Payment Section - Aperçu verrouillé LV3 AVANT le diagnostic :
            sur mobile (81% du trafic), le CTA doit être atteint en 1-2 scrolls,
            pas après un mur de texte. */}
        {!isPaid && (
          <LV3Preview
            teaser={dossier.LV3_teaser}
            onUnlock={handlePayment}
            paymentLoading={paymentLoading}
            offerLetter={isVariantB}
          />
        )}

        {/* Livrable payant version B : courrier de demande de pièces prêt à poster,
            mis en avant AVANT l'analyse juridique. */}
        {isPaid && isVariantB && <DemandePiecesLetter dossierId={dossierId} />}

        {/* Contestation devant le juge de l'exécution — uniquement si une mesure
            d'exécution est identifiée dans l'analyse. Le délai est court et l'inaction
            coûte le recours, donc ce bloc passe avant le diagnostic. */}
        {isPaid && hasMesureExecution(dossier) && <SaisineJexPack dossierId={dossierId} />}

        {/* LV2: Diagnostic - Visible pour TOUS (anonymes et authentifiés), replié par défaut */}
        <LV2Diagnostic lv2Data={lv2Data} />

        {/* LV3: Analyse juridique complète - Visible après paiement */}
        {isPaid && <LV3LegalAnalysis lv3Data={lv3Data} />}

        {/* Prompt création de compte pour anonymes (après le CTA paiement) */}
        {isAnonymous && (
          <div className="card p-6 mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h4 className="font-semibold text-blue-900 text-lg">
                  Sauvegardez votre analyse
                </h4>
                <p className="text-sm text-blue-700 mt-1">
                  Créez un compte gratuit pour conserver votre dossier et y accéder à tout moment.
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  size="sm"
                  onClick={() => {
                    sessionStorage.setItem('link_dossier_id', dossierId);
                    sessionStorage.setItem('link_temp_token', tempToken);
                    if (dossier?.email) sessionStorage.setItem('link_dossier_email', dossier.email);
                    navigate(`/register?from=score&dossier_id=${dossierId}`);
                  }}
                  style={{ backgroundColor: '#1e3a8a', color: 'white' }}
                >
                  Créer un compte gratuit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    sessionStorage.setItem('link_dossier_id', dossierId);
                    sessionStorage.setItem('link_temp_token', tempToken);
                    if (dossier?.email) sessionStorage.setItem('link_dossier_email', dossier.email);
                    navigate(`/login?from=score&dossier_id=${dossierId}`);
                  }}
                  className="text-blue-900"
                >
                  Se connecter
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScorePage;